/** @format */

import { useState, useEffect } from 'react';
import TextInput from '../../../components/ui/TextInput';
import ImageUpload from '../../../components/ui/ImageUpload';
import { slugify } from '../../../utils/helpers';
import TextAreaInput from '../../../components/ui/TextAreaInput';
import TextEditor from '../../../components/ui/TextEditor';
import { AuthButton } from '../../../components/common/AuthButton';

import axios from 'axios';
import { useAuthStore } from '../../../context/useAuthStore';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../constant/routes';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const CreateBlogPostPage: React.FC = () => {
	const navigate = useNavigate();
	const { token } = useAuthStore();
	const [title, setTitle] = useState('');
	const [slug, setSlug] = useState('');

	useEffect(() => {
		const generatedSlug = slugify(title);
		setSlug(generatedSlug);
	}, [title]);

	const [image, setImage] = useState<File | null>(null);
	const [shortDescription, setShortDescription] = useState('');
	const [content, setContent] = useState('');
	const [loading, setLoading] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!title || !slug || !image || !shortDescription || !content) {
			toast.error('Please fill in all fields');
			return;
		}

		setLoading(true);

		try {
			// 1. Upload Image First
			let imageUrl = '';
			if (image) {
				const imageFormData = new FormData();
				imageFormData.append('image', image);

				const uploadResponse = await axios.post(
					`${API_BASE_URL}/imageupload`,
					imageFormData,
					{
						headers: {
							'Content-Type': 'multipart/form-data',
							Authorization: token ? `Bearer ${token}` : '',
						},
					}
				);

				if (
					uploadResponse.data &&
					uploadResponse.data.success &&
					uploadResponse.data.data &&
					uploadResponse.data.data.imageUrl
				) {
					imageUrl = uploadResponse.data.data.imageUrl;
				} else {
					throw new Error('Image upload failed');
				}
			}

			// 2. Create Blog Post with Image URL
			const blogData = {
				title,
				slug,
				shortDescription,
				excerpt: shortDescription,
				content,
				image: imageUrl,
				date: new Date().toISOString().split('T')[0],
			};

			await axios.post(`${API_BASE_URL}/blog`, blogData, {
				headers: {
					'Content-Type': 'application/json',
					Authorization: token ? `Bearer ${token}` : '',
				},
			});

			toast.success('Blog post created successfully!');
			navigate(ROUTES.ALL_BLOG_POSTS); // Redirect if needed, uncomment later
			// Reset form
			setTitle('');
			setSlug('');
			setImage(null);
			setShortDescription('');
			setContent('');
			navigate('/admin/all-blog-posts');
		} catch (error: any) {
			console.error('Error creating blog post:', error);
			const message =
				error.response?.data?.message || 'Failed to create blog post';
			toast.error(message);
		} finally {
			setLoading(false);
		}
	};

	return (
		<section>
			<h1 className='text-2xl font-bold'>Create a New Blog Post</h1>
			<form
				className='space-y-4 p-4'
				onSubmit={handleSubmit}>
				<TextInput
					id='title'
					name='title'
					label='Title'
					type='text'
					value={title}
					onChange={(e) => setTitle(e.target.value)}
					placeholder='Enter title'
					required
					errorMessage={'This field is required'}
				/>
				<p className='text-xs text-myDark pl-4'>
					<b>Slug:</b>{' '}
					<span className='text-myDark font-semibold lowercase'>{slug}</span>
				</p>
				<div className='grid md:grid-cols-2 grid-cols-1 gap-4'>
					<ImageUpload
						id='featureImage'
						name='featureImage'
						label='Feature Image'
						value={image}
						onChange={setImage}
						required
						errorMessage='Please upload a feature image'
					/>
					<TextAreaInput
						id='shortDescription'
						name='shortDescription'
						label='Short Description'
						value={shortDescription}
						onChange={(e) => setShortDescription(e.target.value)}
						required
						errorMessage='This field is required'
					/>
				</div>
				<TextEditor
					label='Content'
					value={content}
					onChange={setContent}
					error={false} // Add validation logic if needed
					errorMessage='Content is required'
				/>
				<AuthButton
					title='Create Blog Post'
					loadingTitle='Creating...'
					loading={loading}
					disabled={
						loading || !title || !image || !shortDescription || !content
					}
				/>
			</form>
		</section>
	);
};

export default CreateBlogPostPage;
